
import { useState, useCallback } from 'react';
import { 
  fetchNewsletterSubscribers, 
  toggleSubscriberStatus, 
  deleteSubscriber,
  sendNewsletter
} from '@/services/admin';
import { toast } from '@/hooks/use-toast';

export const useNewsletterSubscribers = () => {
  const [subscribers, setSubscribers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  // Load newsletter subscribers
  const loadSubscribers = useCallback(async () => {
    setIsLoading(true);
    try {
      const { subscribers, error } = await fetchNewsletterSubscribers();
      
      if (error) {
        toast({
          title: "Error loading subscribers",
          description: error,
          variant: "destructive"
        });
        return;
      }
      
      setSubscribers(subscribers);
    } catch (error) {
      console.error("Error in loadSubscribers:", error);
      toast({
        title: "Error loading subscribers",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Toggle subscriber active status
  const handleToggleStatus = useCallback(async (subscriber: any) => {
    setIsProcessing(true);
    try {
      const newStatus = !subscriber.is_active;
      const { error } = await toggleSubscriberStatus(subscriber.id, newStatus);
      
      if (error) {
        toast({
          title: "Error updating subscriber",
          description: error,
          variant: "destructive"
        });
        return;
      }
      
      // Update local state
      setSubscribers(prev => 
        prev.map(sub => 
          sub.id === subscriber.id ? { ...sub, is_active: newStatus } : sub
        )
      );
      
      toast({
        title: "Subscriber updated",
        description: `${subscriber.email} is now ${newStatus ? 'active' : 'inactive'}`,
      });
    } catch (error) {
      console.error("Error in handleToggleStatus:", error);
      toast({
        title: "Error updating subscriber",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Delete subscriber
  const handleDeleteSubscriber = useCallback(async (subscriber: any) => {
    setIsProcessing(true);
    try {
      const { error } = await deleteSubscriber(subscriber.id);
      
      if (error) {
        toast({
          title: "Error deleting subscriber",
          description: error,
          variant: "destructive"
        });
        return;
      }
      
      // Update local state
      setSubscribers(prev => prev.filter(sub => sub.id !== subscriber.id));
      
      toast({
        title: "Subscriber deleted",
        description: `${subscriber.email} has been removed from the newsletter list`,
      });
    } catch (error) {
      console.error("Error in handleDeleteSubscriber:", error);
      toast({
        title: "Error deleting subscriber",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Send newsletter
  const handleSendNewsletter = useCallback(async (subject: string, content: string) => {
    setIsSending(true);
    try {
      const { result, error } = await sendNewsletter(subject, content);
      
      if (error) {
        toast({
          title: "Error sending newsletter",
          description: error,
          variant: "destructive"
        });
        return false;
      }
      
      toast({
        title: "Newsletter sent",
        description: `Successfully sent to ${result.successful} subscribers`,
      });
      return true;
    } catch (error) {
      console.error("Error in handleSendNewsletter:", error);
      toast({
        title: "Error sending newsletter",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsSending(false);
    }
  }, []);

  return {
    subscribers,
    isLoading,
    isProcessing,
    isSending,
    loadSubscribers,
    handleToggleStatus,
    handleDeleteSubscriber,
    handleSendNewsletter
  };
};
